"use client";

import { motion, useReducedMotion } from "framer-motion";
import type { ElementType } from "react";
import { EASE_SMOOTH, STAGGER_CHILDREN } from "@/components/motion/constants";
import { cn } from "@/lib/utils";

type TextRevealTag = "h1" | "h2" | "h3" | "p" | "span" | "div";

interface TextRevealProps {
  text: string;
  as?: TextRevealTag;
  className?: string;
  delay?: number;
  /** Animate on mount or when scrolled into view. */
  trigger?: "mount" | "inView";
  once?: boolean;
  /** Stagger delay between characters (seconds). */
  charStagger?: number;
}

const CHAR_REVEAL = {
  hidden: { opacity: 0, y: "0.35em" },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: EASE_SMOOTH },
  },
};

const MOTION_TAGS = {
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  p: motion.p,
  span: motion.span,
  div: motion.div,
} as const;

function splitWords(text: string): string[] {
  return text.split(/(\s+)/);
}

export function TextReveal({
  text,
  as = "span",
  className,
  delay = 0,
  trigger = "mount",
  once = true,
  charStagger = STAGGER_CHILDREN * 0.55,
}: TextRevealProps) {
  const reduced = useReducedMotion();
  const Tag = as as ElementType;
  const MotionTag = MOTION_TAGS[as];

  if (reduced) {
    return <Tag className={cn(className)}>{text}</Tag>;
  }

  const words = splitWords(text);
  let charIndex = 0;

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: charStagger,
        delayChildren: delay,
      },
    },
  };

  return (
    <MotionTag
      className={cn(className)}
      variants={containerVariants}
      initial="hidden"
      {...(trigger === "inView"
        ? {
            whileInView: "visible" as const,
            viewport: { once, amount: 0.4 },
          }
        : { animate: "visible" as const })}
      aria-label={text}
    >
      {words.map((word, wordIndex) => {
        if (/^\s+$/.test(word)) {
          return (
            <span key={`space-${wordIndex}`} aria-hidden>
              {word}
            </span>
          );
        }

        return (
          <span
            key={`word-${wordIndex}`}
            className="inline-block whitespace-nowrap"
            aria-hidden
          >
            {Array.from(word).map((char) => {
              const i = charIndex++;
              return (
                <motion.span
                  key={`${i}-${char}`}
                  className="inline-block"
                  variants={CHAR_REVEAL}
                >
                  {char}
                </motion.span>
              );
            })}
          </span>
        );
      })}
    </MotionTag>
  );
}
